import type { AgreementDocument } from "./document";

/**
 * VetBoats User Agreement, Assumption of Risk, Waiver and Release.
 * Draft v1 for testing, pending attorney review. Changing ANY wording requires a new
 * `version`, which makes everyone sign again before using the site.
 */
export const USER_AGREEMENT: AgreementDocument = {
  kind: "user",
  version: "v1 (2026-10-02, draft pending attorney review)",
  title: "VetBoats User Agreement, Assumption of Risk, Waiver and Release",
  notice:
    "PLEASE READ CAREFULLY. THIS AGREEMENT GIVES UP LEGAL RIGHTS, INCLUDING YOUR RIGHT TO SUE FOR INJURY, DEATH, OR PROPERTY DAMAGE. YOU MUST ACCEPT IT TO USE VETBOATS.COM.",
  facts: [],
  sections: [
    {
      heading: "1. Who this agreement covers",
      blocks: [
        {
          type: "paragraph",
          text: '1.1 "VetBoats" means the vetboats.com website and its operator, Ryan Tomlinson, together with anyone who volunteers to help run it.',
        },
        {
          type: "paragraph",
          text: '1.2 "Owner" means any person who lists a boat on VetBoats. "Borrower" means any person who requests, accepts, operates, or rides on a boat found through VetBoats. "Guest" means anyone the Borrower brings aboard.',
        },
        {
          type: "paragraph",
          text: '1.3 "Released Parties" means VetBoats and every Owner, together with their family members, heirs, and insurers.',
        },
        {
          type: "paragraph",
          text: '1.4 "You" means the person accepting this agreement, whether as Owner, Borrower, Guest, or visitor. You accept it for yourself, and for your heirs, estate, spouse, children, and anyone else who might bring a claim through you.',
        },
      ],
    },
    {
      heading: "2. No other organization is involved",
      blocks: [
        {
          type: "paragraph",
          text: "2.1 VetBoats is an independent, private arrangement between individual boat owners and individual borrowers. It is NOT sponsored, endorsed, operated, supervised, or funded by any veterans' service organization, military unit, government agency, charity, or other group, including any organization whose members may have told you about VetBoats.",
        },
        {
          type: "paragraph",
          text: "2.2 No such organization has any role in, knowledge of, or responsibility for any boat loan, and you will not bring any claim against any such organization arising from VetBoats.",
        },
      ],
    },
    {
      heading: "3. What VetBoats does and doesn't do",
      blocks: [
        {
          type: "paragraph",
          text: "3.1 VetBoats is only a listing and messaging tool that lets Owners and Borrowers find each other. VetBoats does not own, inspect, maintain, insure, or operate any boat.",
        },
        {
          type: "paragraph",
          text: "3.2 VetBoats does NOT verify anyone's identity, military service, boating experience, licenses, criminal history, sobriety, or insurance, and does not check any boat's condition or safety equipment. Any information on the site comes from users, and VetBoats makes no promise that it is true.",
        },
        {
          type: "paragraph",
          text: "3.3 Every loan is a private agreement between the Owner and the Borrower. VetBoats is not a party to it.",
        },
        {
          type: "paragraph",
          text: "3.4 No money or other payment may change hands for the use of a boat, through VetBoats or outside it. A Borrower may replace fuel they used.",
        },
      ],
    },
    {
      heading: "4. Risks you are accepting",
      blocks: [
        {
          type: "paragraph",
          text: "4.1 You understand that boating and water activities are inherently dangerous, and can cause serious injury, drowning, death, and property loss. The risks include, among others: capsizing, sinking, collision, grounding, fire, explosion, carbon monoxide poisoning, falls, propeller strikes, hypothermia, sudden weather and water changes, mechanical or equipment failure, hidden defects in the boat, other boaters' actions, and delay in rescue or medical care.",
        },
        {
          type: "paragraph",
          strong: true,
          text: "4.2 You knowingly and voluntarily assume all of these risks, known and unknown, including risks arising from the ordinary negligence of any Released Party.",
        },
        {
          type: "paragraph",
          text: "4.3 You are responsible for deciding whether you are physically, mentally, and legally fit to take part, and whether a boat is safe to use. If you are not sure, don't go.",
        },
      ],
    },
    {
      heading: "5. Release and waiver",
      blocks: [
        {
          type: "paragraph",
          strong: true,
          text: "5.1 To the fullest extent allowed by law, you release, waive, and promise not to sue any Released Party for any injury, illness, death, property damage, or other loss arising from VetBoats, any boat loan, or the use of any boat, including losses caused by the ordinary negligence of a Released Party.",
        },
        {
          type: "paragraph",
          text: "5.2 This release does not cover a Released Party's gross negligence, recklessness, or intentional misconduct, or anything else the law does not allow to be waived.",
        },
      ],
    },
    {
      heading: "6. Borrower responsibilities",
      blocks: [
        {
          type: "paragraph",
          text: "6.1 While a boat is in the Borrower's control, from the moment it is handed over until it is returned to the Owner, the Borrower is solely responsible for it and for everyone aboard.",
        },
        { type: "paragraph", text: "6.2 The Borrower will:" },
        {
          type: "list",
          items: [
            "hold every boating license, safety certificate, and registration required where the boat is used;",
            "inspect the boat before use and not use it if it seems unsafe;",
            "carry and use required safety equipment, including a properly fitting life jacket for each person aboard;",
            "follow all boating laws, navigation rules, and the Owner's instructions;",
            "not operate the boat, or let anyone else operate it, under the influence of alcohol or drugs;",
            "not let anyone operate the boat unless the Owner has approved them; and",
            "make sure every person aboard has signed this agreement before boarding, as described in Section 6.3.",
          ],
        },
        {
          type: "paragraph",
          strong: true,
          text: "6.3 Getting everyone aboard to sign is the Borrower's responsibility alone. Before departure, the Borrower must make sure that every adult aboard has signed this agreement, using the guest waiver link VetBoats provides for that trip, and that a parent or legal guardian has signed it for every minor aboard. VetBoats and the Owner have no duty to check. If anyone boards without signing, the Borrower is fully responsible for, and will defend, indemnify, and hold harmless every Released Party against, any claim by or through that person.",
        },
      ],
    },
    {
      heading: "7. Borrower pays for harm to others",
      blocks: [
        {
          type: "paragraph",
          strong: true,
          text: "7.1 The Borrower is fully responsible for, and will pay, all injury, death, and damage to any person or property other than the loaned boat itself that happens while the boat is in the Borrower's control. This includes injury to Guests, other boaters, and swimmers; damage to docks, other boats, and property; fines and penalties; fuel spills and pollution cleanup; and the cost of towing, salvage, raising, and removing the boat if it becomes disabled, grounded, or sinks.",
        },
        {
          type: "paragraph",
          text: "7.2 Indemnity. The Borrower will defend, indemnify, and hold harmless every Released Party from any claim, demand, lawsuit, cost, or expense (including reasonable attorney fees) arising from the Borrower's or any Guest's use of the boat.",
        },
      ],
    },
    {
      heading: "8. Damage to the loaned boat itself",
      blocks: [
        {
          type: "paragraph",
          strong: true,
          text: "8.1 Unless the Owner has chosen otherwise on the listing, the Owner accepts the risk of loss or damage to the loaned boat and its equipment, and will not seek payment from the Borrower to repair or replace it.",
        },
        {
          type: "paragraph",
          text: "8.2 This waiver does not apply if the damage happened while the Borrower or anyone they allowed aboard was: (a) operating under the influence of alcohol or drugs; (b) letting someone the Owner did not approve operate the boat; (c) using the boat for any illegal purpose or for payment; or (d) acting intentionally or recklessly.",
        },
        {
          type: "paragraph",
          text: "8.3 If the Owner's listing says \"Borrower pays for damage,\" the Borrower is responsible for the reasonable cost to repair the boat, or its fair market value if it cannot be repaired, less any amount the Owner's insurer pays.",
        },
        {
          type: "paragraph",
          text: "8.4 Nothing in this Section 8 limits the Borrower's responsibility under Section 7 for harm to anyone or anything other than the loaned boat.",
        },
      ],
    },
    {
      heading: "9. Owner responsibilities",
      blocks: [
        {
          type: "paragraph",
          text: "9.1 The Owner will describe the boat honestly, disclose every known defect or safety problem, keep required safety equipment aboard, and keep the boat's registration current.",
        },
        {
          type: "paragraph",
          text: "9.2 The Owner is responsible for confirming that their own insurance covers lending the boat, and understands that VetBoats has not reviewed it.",
        },
        {
          type: "paragraph",
          text: "9.3 The Owner releases VetBoats from any claim arising from a Borrower's use of, or damage to, the Owner's boat.",
        },
      ],
    },
    {
      heading: "10. General terms",
      blocks: [
        {
          type: "paragraph",
          text: '10.1 No warranties. VetBoats is provided "as is." VetBoats makes no promises about any boat, any user, or the site itself.',
        },
        {
          type: "paragraph",
          text: "10.2 Limit on VetBoats' liability. If VetBoats is ever found liable despite this agreement, its total liability to you will not exceed $100.",
        },
        {
          type: "paragraph",
          text: "10.3 Governing law and venue. This agreement is governed by the laws of the State of Oregon, and by federal maritime law where it applies. Any lawsuit must be brought in the state or federal courts of Deschutes County, Oregon.",
        },
        {
          type: "paragraph",
          text: "10.4 Severability. If any part of this agreement is found unenforceable, that part will be enforced to the greatest extent allowed, and the rest remains in full effect.",
        },
        {
          type: "paragraph",
          text: "10.5 Entire agreement and changes. This agreement, together with any Boat Loan Agreement for a specific reservation, is the entire agreement about its subject. VetBoats may update this agreement. You will be asked to accept any new version before using the site again.",
        },
        {
          type: "paragraph",
          text: "10.6 Age. You must be at least 18 years old to accept this agreement. Minors may go aboard only with a parent or legal guardian who accepts this agreement on the minor's behalf, to the extent the law allows.",
        },
      ],
    },
    {
      heading: "11. Electronic signature",
      blocks: [
        {
          type: "paragraph",
          text: '11.1 You agree that checking the box, typing your full legal name, and selecting "Sign" is your electronic signature, has the same effect as a handwritten signature, and satisfies the federal E-SIGN Act and applicable state electronic-transactions laws.',
        },
        {
          type: "paragraph",
          text: "11.2 VetBoats keeps a permanent record of your signature: the name you typed, the date and time, the IP address and web browser you used, the version of this agreement, and its exact text. VetBoats emails a copy of what you signed to the email address you provide.",
        },
        {
          type: "paragraph",
          strong: true,
          text: "11.3 BY SIGNING, YOU CONFIRM THAT YOU HAVE READ THIS AGREEMENT, UNDERSTAND IT, ARE GIVING UP SUBSTANTIAL LEGAL RIGHTS, AND SIGN IT FREELY.",
        },
      ],
    },
  ],
};
